import { useState, useEffect } from 'react';
import { Usd, PriceCaption } from '@/utils/CurrencyContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/utils/api';
import { toast } from 'sonner';
import { ShoppingCart, ArrowLeft, Trash2, Plus, Minus, Package, CreditCard, Loader2, Truck, Download } from 'lucide-react';
import { motion } from 'framer-motion';

const CartPage = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [shippingInfo, setShippingInfo] = useState({
    address: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    phone: ''
  });

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/marketplace/cart');
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      toast.error('Failed to load cart');
    } finally {
      setIsLoading(false);
    }
  };

  const updateQuantity = async (itemId, newQuantity) => {
    try {
      await api.put(`/marketplace/cart/update/${itemId}`, null, {
        params: { quantity: newQuantity }
      });
      fetchCart();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update quantity');
    }
  };

  const removeItem = async (itemId) => {
    try {
      await api.delete(`/marketplace/cart/remove/${itemId}`);
      toast.success('Item removed from cart');
      fetchCart();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const clearCart = async () => {
    try {
      await api.delete('/marketplace/cart/clear');
      toast.success('Cart cleared');
      fetchCart();
    } catch (error) {
      toast.error('Failed to clear cart');
    }
  };

  const handleCheckout = async () => {
    if (cart.has_physical_products) {
      // Validate shipping info
      if (!shippingInfo.address || !shippingInfo.city || !shippingInfo.country) {
        toast.error('Please fill in required shipping information');
        return;
      }
    }

    setIsCheckingOut(true);
    try {
      const response = await api.post('/marketplace/checkout/create-paypal-order', shippingInfo);
      
      // Redirect to PayPal approval URL
      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      } else {
        toast.error('Failed to create PayPal order');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed. Please try again.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/marketplace')}
              className="mb-2 -ml-2"
              style={{ color: '#5C635A' }}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Continue Shopping
            </Button>
            <h1
              className="text-3xl font-bold tracking-tight"
              style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
            >
              Shopping Cart
            </h1>
          </div>
          {cart && cart.items.length > 0 && (
            <Button
              variant="ghost"
              onClick={clearCart}
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear Cart
            </Button>
          )}
        </div>

        {!cart || cart.items.length === 0 ? (
          <Card className="text-center py-16" style={{ borderColor: '#E3E1DB' }}>
            <CardContent>
              <ShoppingCart className="h-16 w-16 mx-auto mb-4" style={{ color: '#E3E1DB' }} />
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#163329' }}>Your cart is empty</h3>
              <p className="mb-6" style={{ color: '#5C635A' }}>Browse our marketplace to find great products!</p>
              <Button
                onClick={() => navigate('/marketplace')}
                className="rounded-full"
                style={{ backgroundColor: '#163329' }}
              >
                Browse Products
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item, idx) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card style={{ borderColor: '#E3E1DB' }}>
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {/* Product Image */}
                        <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                          {item.product.images && item.product.images.length > 0 ? (
                            <img
                              src={item.product.images[0]}
                              alt={item.product.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="h-8 w-8" style={{ color: '#E3E1DB' }} />
                            </div>
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h3 className="font-semibold line-clamp-1" style={{ color: '#163329' }}>
                                {item.product.title}
                              </h3>
                              <p className="text-sm" style={{ color: '#5C635A' }}>
                                <Usd value={item.product.price} /> each
                              </p>
                              {item.product.is_digital && (
                                <span className="inline-flex items-center text-xs text-blue-600 mt-1">
                                  <Download className="h-3 w-3 mr-1" />
                                  Digital Download
                                </span>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeItem(item.id)}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>

                          {/* Quantity & Total */}
                          <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 rounded-full"
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                disabled={item.quantity <= 1}
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-8 text-center font-medium">{item.quantity}</span>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 rounded-full"
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                disabled={!item.product.is_digital && item.quantity >= item.product.stock}
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                            <span className="font-bold text-lg" style={{ color: '#163329' }}>
                              <Usd value={item.item_total} />
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="space-y-4">
              <Card style={{ borderColor: '#E3E1DB' }}>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    Order Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <span style={{ color: '#5C635A' }}>Items ({cart.item_count})</span>
                    <span style={{ color: '#163329' }}><Usd value={cart.total} /></span>
                  </div>
                  <div className="border-t pt-4" style={{ borderColor: '#E3E1DB' }}>
                    <div className="flex justify-between font-bold text-lg">
                      <span style={{ color: '#163329' }}>Total</span>
                      <span style={{ color: '#163329' }}><Usd value={cart.total} /></span>
                    </div>
                    <PriceCaption className="font-normal text-right" />
                  </div>

                  {/* Shipping Form */}
                  {cart.has_physical_products && !showCheckoutForm && (
                    <div className="flex items-center gap-2 text-sm p-3 rounded-lg" style={{ backgroundColor: '#FFF8E1' }}>
                      <Truck className="h-4 w-4" style={{ color: '#E2A761' }} />
                      <span style={{ color: '#5C635A' }}>Shipping address required</span>
                    </div>
                  )}

                  {cart.has_physical_products && showCheckoutForm && (
                    <div className="space-y-3 pt-4 border-t" style={{ borderColor: '#E3E1DB' }}>
                      <h4 className="font-semibold" style={{ color: '#163329' }}>Shipping Address</h4>
                      <div>
                        <Label htmlFor="address">Address *</Label>
                        <Input
                          id="address"
                          value={shippingInfo.address}
                          onChange={(e) => setShippingInfo({...shippingInfo, address: e.target.value})}
                          placeholder="123 Main St"
                          className="mt-1"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label htmlFor="city">City *</Label>
                          <Input
                            id="city"
                            value={shippingInfo.city}
                            onChange={(e) => setShippingInfo({...shippingInfo, city: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="state">State</Label>
                          <Input
                            id="state"
                            value={shippingInfo.state}
                            onChange={(e) => setShippingInfo({...shippingInfo, state: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label htmlFor="zip">ZIP Code</Label>
                          <Input
                            id="zip"
                            value={shippingInfo.zip}
                            onChange={(e) => setShippingInfo({...shippingInfo, zip: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="country">Country *</Label>
                          <Input
                            id="country"
                            value={shippingInfo.country}
                            onChange={(e) => setShippingInfo({...shippingInfo, country: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          value={shippingInfo.phone}
                          onChange={(e) => setShippingInfo({...shippingInfo, phone: e.target.value})}
                          placeholder="+1 234 567 8900"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  )}

                  {cart.has_physical_products && !showCheckoutForm ? (
                    <Button
                      className="w-full rounded-full"
                      style={{ backgroundColor: '#163329' }}
                      onClick={() => setShowCheckoutForm(true)}
                    >
                      Enter Shipping Info
                    </Button>
                  ) : (
                    <Button
                      className="w-full rounded-full"
                      style={{ backgroundColor: '#163329' }}
                      onClick={handleCheckout}
                      disabled={isCheckingOut}
                      data-testid="checkout-btn"
                    >
                      {isCheckingOut ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="h-4 w-4 mr-2" />
                          Checkout with PayPal
                        </>
                      )}
                    </Button>
                  )}

                  <p className="text-xs text-center" style={{ color: '#5C635A' }}>
                    Payment processed securely via PayPal
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
